import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  fetchRosterOverrides,
  hideRosterPlayer,
  unhideRosterPlayer,
  addRosterPlayer,
  removeRosterPlayer,
} from "./rosterServices";
import { Badge, BmModal, Btn, EmptyState, Input, Overline } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import "../../ForBrands/brandMode.css";

const rowId = (r) => r.sportsRadarId || r.cerebroId || r.id || "";
const rowName = (r) => r.playerName || `${r.firstName || ""} ${r.lastName || ""}`.trim() || rowId(r);

/* Roster Manager (V2 style): the coach / admin control over any roster
   source. Hide a player the source still lists, add a player the source is
   missing, restore hidden players. Adding needs only the full name: the
   email is optional, and only links the player's CACHE account when they
   already have one (no invite is sent; a player without an account joins
   with the team code). Changes persist per team and apply everywhere the
   roster is loaded; the parent re-runs the estimate on change.
   Inside the Coach Mode shell on a phone (D-c7) it opens full screen: the
   close X and the title in the header, one column, the whole list in the
   page scroll, 56px fields (a shorter email placeholder that fits; the note
   under the form says what linking does) and a full-width 52px "Add to
   roster". Elsewhere (Player Index, Admin) it stays the centred dialog. */
const RosterManagerModal = ({ open, onClose, teamId, league, teamName, roster, onChanged }) => {
  const phone = usePhoneTheme();
  const [data, setData] = useState({ hidden: [], added: [], canManage: false });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ fullName: "", position: "", jerseyNumber: "", athleteEmail: "" });

  useEffect(() => {
    if (!open || !teamId) return;
    setLoading(true);
    fetchRosterOverrides(teamId)
      .then(setData)
      .catch(() => toast.error("Could not load roster settings."))
      .finally(() => setLoading(false));
  }, [open, teamId]);

  if (!open) return null;

  // Resolves true once the change is saved, false when it was refused (the
  // error toast says why).
  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      const next = await fn();
      setData(next);
      if (successMsg) toast.success(successMsg);
      onChanged?.();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Roster update failed.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const hiddenIds = new Set((data.hidden || []).map((h) => h.playerId));
  const addedIds = new Set((data.added || []).map((a) => a.playerId));
  const visible = (roster || []).filter((r) => rowId(r) && !hiddenIds.has(rowId(r)));

  // Only the full name is required; a blank email adds the player without
  // linking an account. The form clears once the player is added; a refused
  // add (an email with no CACHE account) keeps what was typed, so the coach
  // can clear the email and add the player.
  const submitAdd = (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) return;
    run(
      () => addRosterPlayer({ teamId, league, teamName, ...form, fullName: form.fullName.trim(), athleteEmail: form.athleteEmail.trim() }),
      `${form.fullName.trim()} added to the roster.`,
    ).then((added) => added && setForm({ fullName: "", position: "", jerseyNumber: "", athleteEmail: "" }));
  };

  return (
    <BmModal title={`Manage roster${teamName ? ` · ${teamName}` : ""}`} onClose={onClose} width={920} phonePresentation="fullscreen">
      {loading ? (
        <div className="py-10 text-center bm-body-m text-bm-muted">Loading roster settings...</div>
      ) : !data.canManage ? (
        <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-5 py-4 bm-body-m text-bm-ink">
          You don't manage this team's roster. Coaches assigned to the team and admins can hide or add players.
        </div>
      ) : (
        <div className={phone ? "bm-phone-form flex flex-col gap-8" : "grid gap-8 md:grid-cols-2"}>
          {/* On the roster */}
          <div className="flex flex-col gap-3">
            <Overline>On the roster · {visible.length}</Overline>
            <ul className={phone ? "rounded-[12px] border border-bm-border" : "max-h-[46vh] overflow-y-auto rounded-[8px] border border-bm-border"}>
              {visible.map((r) => {
                const id = rowId(r);
                const isAdded = addedIds.has(id) || String(id).startsWith("ovr-");
                return (
                  <li key={id} className="flex items-center justify-between gap-3 border-b border-bm-mutedbg px-4 py-[10px] last:border-b-0">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate bm-h-xs text-bm-ink">{rowName(r)}</span>
                        <span className="bm-body-s text-bm-muted">{[r.position, r.jersey ? `#${r.jersey}` : ""].filter(Boolean).join(" · ") || "No position listed"}</span>
                      </span>
                      {isAdded && <Badge tone="soft">Added</Badge>}
                    </span>
                    {isAdded ? (
                      <Btn variant="danger" size="sm" disabled={busy} onClick={() => run(() => removeRosterPlayer({ teamId, playerId: id }), `${rowName(r)} removed.`)} title="Remove this added player">
                        Remove
                      </Btn>
                    ) : (
                      <Btn
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => run(() => hideRosterPlayer({ teamId, playerId: id, name: rowName(r), league, teamName }), `${rowName(r)} hidden from the roster.`)}
                        title="Hide from the roster (transferred out, for example)"
                      >
                        Hide
                      </Btn>
                    )}
                  </li>
                );
              })}
              {visible.length === 0 && <li className="px-4 py-8 text-center bm-body-m text-bm-muted">No players on the roster.</li>}
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            {/* Hidden */}
            <div className="flex flex-col gap-3">
              <Overline>Hidden · {(data.hidden || []).length}</Overline>
              {(data.hidden || []).length === 0 ? (
                <EmptyState>Nobody hidden.</EmptyState>
              ) : (
                <ul className={phone ? "rounded-[12px] border border-bm-border" : "rounded-[8px] border border-bm-border"}>
                  {data.hidden.map((h) => (
                    <li key={h.playerId} className="flex items-center justify-between gap-3 border-b border-bm-mutedbg px-4 py-[10px] last:border-b-0">
                      <span className="truncate bm-h-xs text-bm-ink">{h.name || h.playerId}</span>
                      <Btn variant="ghost" size="sm" disabled={busy} onClick={() => run(() => unhideRosterPlayer({ teamId, playerId: h.playerId }), `${h.name || "Player"} restored.`)}>
                        Restore
                      </Btn>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Add */}
            <form onSubmit={submitAdd} className="flex flex-col gap-3">
              <Overline>Add a player</Overline>
              <div className={phone ? "grid grid-cols-2 gap-3" : "grid grid-cols-2 gap-2"}>
                <Input type="text" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} placeholder="Full name" className="col-span-2" required />
                <Input type="text" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Position (G, F, C)" />
                <Input
                  type="text"
                  inputMode={phone ? "numeric" : undefined}
                  value={form.jerseyNumber}
                  onChange={(e) => setForm({ ...form, jerseyNumber: e.target.value })}
                  placeholder="Jersey number"
                />
                <Input
                  type="email"
                  value={form.athleteEmail}
                  onChange={(e) => setForm({ ...form, athleteEmail: e.target.value })}
                  placeholder={phone ? "CACHE account email (optional)" : "CACHE account email (optional, links their profile)"}
                  aria-describedby="roster-add-email-note"
                  className="col-span-2"
                />
              </div>
              <div>
                <Btn type="submit" size={phone ? "lg" : undefined} className={phone ? "w-full" : undefined} disabled={busy || !form.fullName.trim()}>
                  Add to roster
                </Btn>
              </div>
              <p id="roster-add-email-note" className="bm-body-s text-bm-tertiary">
                The email is optional. Leave it blank to add the player now; no invite is sent. The email of their CACHE account links
                their profile to this team so their stats and score flow in. Players without an account join with your team code.
              </p>
            </form>
          </div>
        </div>
      )}
    </BmModal>
  );
};

export default RosterManagerModal;
